import { Component, EventEmitter, Input, inject, Output } from '@angular/core';
import { ComponentsModule, DialogService, SnackBarService, TabItem } from '@mastechnicaegroup/carelinelive-ui';
import { RefreshableResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { RegionSingleResponse } from '@lib/api/models/region';
import { AsyncPipe } from '@angular/common';
import { StoreZoneRequest, Zone, Zone as ZoneData, ZoneSingleResponse } from '@lib/api/models/zone';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { RegionService } from '@lib/region/region.service';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { PromptRegionZoneStatusChangeDialogComponent } from '../../../../dialogs/prompt/zone/prompt-region-zone-status-change-dialog.component';
import { catchError, of, tap } from 'rxjs';

@Component({
    selector: 'app-region-zones-tab',
    imports: [ComponentsModule, AsyncPipe],
    templateUrl: './region-zones-tab.component.html',
    standalone: true,
    styleUrl: './region-zones-tab.component.scss'
})
export class RegionZonesTabComponent {
    @Input() regionData: RefreshableResult<RegionSingleResponse> | undefined;
    @Output() addZoneEvent: EventEmitter<void> = new EventEmitter<void>();
    @Output() editZoneEvent: EventEmitter<ZoneData> = new EventEmitter<ZoneData>();

    private readonly _regionService = inject(RegionService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _authService = inject(AuthenticationService);
    private readonly _dialog = inject(DialogService);

    protected user = toSignal(this._authService.user$);
    private currentZoneId = '';

    protected readonly tabItems: TabItem[] = [
        {
            disabled: false,
            id: '1',
            title: 'All'
        },
        {
            disabled: false,
            id: '2',
            title: 'Active'
        },
        {
            disabled: false,
            id: '3',
            title: 'Inactive'
        }
    ];

    updateZoneRequest = new SendableRequest<ZoneSingleResponse, StoreZoneRequest>({
        request: (payload) => this._regionService.updateZone(this.currentZoneId, payload!),
        onSuccess: () => {
            this._snackBar.open({
                colour: 'success',
                dismissable: false,
                icon: 'check_circle',
                title: 'Zone status edited successfully',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });

            this.currentZoneId = '';
        },
        onError: () => {
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'Zone status failed to change',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });
        }
    });

    addZone() {
        this.addZoneEvent.emit();
    }

    editZone(zone: ZoneData) {
        this.editZoneEvent.emit(zone);
    }

    changeStatus(zone: Zone, status: boolean) {
        const zoneData = {
            ...zone,
            status: status
        };

        return PromptRegionZoneStatusChangeDialogComponent.show(this._dialog, zoneData).closed.pipe(
            tap(() => {
                this.regionData?.refresh();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Zone failed to change status',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });
                return of(null);
            })
        ).subscribe();
    }

}
